import { useHistory } from 'react-router-dom';
import { useAppContext } from '../../hooks';
import { Text, Stack, Button, Link, Container, List } from 'basis';
import React, { useCallback, useState } from 'react';
import { LayoutPage, Modal } from 'components';
import { Routes } from 'types';
import { config } from '_config';
import { byCountry } from 'utils';
import styled from '@emotion/styled';
import { LimitedWidth } from 'components/layout-page/LimitedWidth';

const ButtonContainer = styled.div`
  width: 100%;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: 250px;
    margin: 0;
  }
`;

const Bold = styled.span`
  font-weight: 600;
`;

export const AchConsents: React.FC = () => {
  const [isAgreeToTerms, setIsAgreeToTerms] = useState(false);
  const [showError, setShowError] = useState(false);
  const history = useHistory();

  const { state } = useAppContext();

  const handleContinue = useCallback(() => {
    if (isAgreeToTerms) {
      setShowError(false);
      history.push(Routes.achGreenIdVerification);
    } else {
      setShowError(true);
      document.getElementById('agreeToTerms')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [history, isAgreeToTerms]);

  const handleCheckboxChange = useCallback(() => {
    setIsAgreeToTerms(!isAgreeToTerms);
    if (showError) {
      setShowError(false);
    }
  }, [isAgreeToTerms, showError]);

  return (
    <LayoutPage step="update" hideProgressStepper>
      <LimitedWidth>
        <Stack gap="8">
          <Text as="h1" textStyle="heading5" textStyle-md="heading4">
            {config.routeConfig[Routes.achConsents].pageTitle}
          </Text>

          {byCountry(state, {
            au: (
              <AuConsent
                isAgreeToTerms={isAgreeToTerms}
                showError={showError}
                handleCheckboxChange={handleCheckboxChange}
              />
            ),
            nz: (
              <NzConsent
                isAgreeToTerms={isAgreeToTerms}
                showError={showError}
                handleCheckboxChange={handleCheckboxChange}
              />
            ),
          })}
          <ButtonContainer>
            <Button onClick={handleContinue} width="100%">
              Continue
            </Button>
          </ButtonContainer>
        </Stack>
      </LimitedWidth>
    </LayoutPage>
  );
};

const TextLink = styled.span`
  text-decoration: underline;
  color: rgb(0, 70, 170);
  cursor: pointer;

  &:hover {
    border-bottom-color: rgb(0, 70, 170);
    background-color: rgb(216, 237, 255);
  }
`;

type AuConsentProps = {
  isAgreeToTerms: boolean;
  showError: boolean;
  handleCheckboxChange: () => void;
};
function AuConsent({ isAgreeToTerms, showError, handleCheckboxChange }: AuConsentProps) {
  const [isDocumentIssuerModalOpen, setIsDocumentIssuerModalOpen] = useState(false);
  const [isCreditReportingBodiesModalOpen, setIsCreditReportingBodiesModalOpen] = useState(false);
  return (
    <>
      {showError && (
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="agreeToTerms" style={{ cursor: 'pointer' }}>
            <Text color="conditional.negative.text">
              <strong>
                Please check the agree indicator to show that you have read and agreed to the Product Disclosure
                Statement.
              </strong>
            </Text>
          </label>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
        <input
          id="agreeToTerms"
          type="checkbox"
          name="agreeToTerms"
          style={{
            width: '20px',
            height: '20px',
            marginTop: '3px',
            cursor: 'pointer',
          }}
          checked={isAgreeToTerms}
          onChange={handleCheckboxChange}
        />
        <div style={{ flex: 1 }}>
          <Stack gap="4">
            <label htmlFor="agreeToTerms" style={{ cursor: 'pointer' }}>
              <Text>
                I hereby apply to Latitude to become an additional cardholder on the above account. I am an Australian
                resident and at least the age of 16.
              </Text>
            </label>

            <Text><Bold>I agree that:</Bold></Text>

            <List type="ordered">
              <List.Item>
                <Text>
                  Each use of the additional card will be subject to the credit contract for the above account;
                </Text>
              </List.Item>
              <List.Item>
                <Text>I will comply with the terms of the credit contract.</Text>
                <div style={{ height: '14px' }} />
                <Text>
                  I acknowledge that: (i) the information provided in this application is true and correct and that no
                  information which is or could be relevant to Latitude making me an additional cardholder has been
                  withheld; and (ii) I have read and understood the{' '}
                  <Link
                    href="https://assets.latitudefinancial.com/legals/credit-card-general-terms-and-conditions.pdf"
                    newTab
                  >
                    Latitude Credit Card General Terms and Conditions and Credit Guide
                  </Link>{' '}
                  provided to the primary cardholder at the time of application.
                </Text>
              </List.Item>
              <List.Item>
                <Text>
                  I understand that Latitude collects and uses the information provided about me to facilitate this
                  authorisation. Further information to Latitude collecting, using and disclosing my personal
                  information in accordance with its privacy policy which is available at{' '}
                  <Link href="https://www.latitudefinancial.com.au/privacy" newTab>
                    www.latitudefinancial.com.au/privacy
                  </Link>
                  .
                </Text>
              </List.Item>
            </List>
          </Stack>
        </div>
      </div>
      <Stack gap="4">
        <div style={{ marginTop: '32px' }}>
          <Text textStyle="heading5">
            <strong>Important Information Notice</strong>
          </Text>
        </div>
        <Text>
          <Bold>To save you time, we can attempt to verify your identity online</Bold>
          <Text textStyle="legal">These checks are secure</Text>
        </Text>

        <Text>
          Once you proceed with the next section, you will be asked to provide further identity information/consent
          to use your information to verify your identity online as explained below. You can choose not to consent
          to providing this information. If you do not consent, simply refer back to the email you received from
          Latitude regarding the request to add you as an additional cardholder and select the Australia Post option
          to verify your identity.
        </Text>

        <List>
          <List.Item>
            <Text>
              You are required to consent to third party checks with{' '}
              <TextLink onClick={() => setIsCreditReportingBodiesModalOpen(true)}>credit reporting bodies</TextLink>
              . This is not a credit check. It simply checks that information such as your name, address, and date
              of birth match what the credit reporting bodies hold.
            </Text>
          </List.Item>
          <List.Item>
            <Text>
              You are required to confirm that you are authorised to provide identity document details (such as a
              driver licence) and agree to them being checked with the{' '}
              <TextLink onClick={() => setIsDocumentIssuerModalOpen(true)}>identification document issuer</TextLink>{' '}
              and consent to using any identity document provided in the application to verify your identity for
              this purpose.
            </Text>
          </List.Item>
        </List>

        <Text>
          The information you provide in your application is collected and stored by Latitude. Some of that
          information; your name, date of birth, address and reference numbers such as your driver licence number
          (together referred to as your personal information) is disclosed to Green ID. Green ID is owned and
          operated by GBG ANZ Pty Ltd which provides electronic identification verification services to Latitude. To
          enable Latitude to verify your identity and meet its legal obligations under Anti-Money Laundering laws,
          Latitude discloses your personal information to Green ID and then Green ID collects, uses and discloses
          your personal information to each of the data sources that you select when you use Green ID. Latitude does
          not collect, use, nor store any of the reference numbers that you disclose within any of the data sources
          that you select and use as part of the Green ID electronic identity verification process. I agree that my
          above information is checked with the Issuer or Official Record Holder
        </Text>
      </Stack>

      <DocumentIssuerModal isOpen={isDocumentIssuerModalOpen} onClose={() => setIsDocumentIssuerModalOpen(false)} />
      <CreditReportingBodiesModal
        isOpen={isCreditReportingBodiesModalOpen}
        onClose={() => setIsCreditReportingBodiesModalOpen(false)}
      />
    </>
  );
}

type NzConsentProps = {
  isAgreeToTerms: boolean;
  showError: boolean;
  handleCheckboxChange: () => void;
};
function NzConsent({ isAgreeToTerms, showError, handleCheckboxChange }: NzConsentProps) {
  const [isDocumentIssuerModalOpen, setIsDocumentIssuerModalOpen] = useState(false);
  const [isCreditReportingBodiesModalOpen, setIsCreditReportingBodiesModalOpen] = useState(false);
  return (
    <>
      {showError && (
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="agreeToTerms" style={{ cursor: 'pointer' }}>
            <Text color="conditional.negative.text">
              <strong>
                Please check the agree indicator to show that you have read and agreed to the Product Disclosure
                Statement.
              </strong>
            </Text>
          </label>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
        <input
          id="agreeToTerms"
          type="checkbox"
          name="agreeToTerms"
          style={{
            width: '20px',
            height: '20px',
            marginTop: '3px',
            cursor: 'pointer',
          }}
          checked={isAgreeToTerms}
          onChange={handleCheckboxChange}
        />
        <div style={{ flex: 1 }}>
          <Stack gap="4">
            <label htmlFor="agreeToTerms" style={{ cursor: 'pointer' }}>
              <Text>
                I apply to Latitude Financial Services Limited (‘Latitude’) to become an additional Gem Visa cardholder
                on the above account. I am a New Zealand resident and at least 16 years of age.
              </Text>
            </label>

            <Text><Bold>I agree that:</Bold></Text>

            <List type="ordered">
              <List.Item>
                <Text>
                  Each use of the additional card will be subject to{' '}
                  <Link
                    href="https://assets.gemfinance.co.nz/legals/conditions-of-use/gemvisa-nz/cou.pdf?_ga=2.236588865.1323252382.1615416779-941858851.1587360214"
                    newTab
                  >
                    Gem Visa Conditions of Use
                  </Link>{' '}
                  and the{' '}
                  <Link href="https://assets.latitudefinancial.com/gem-essentials/nz-gem-visa-ids.pdf" newTab>
                    Initial Disclosure Statement
                  </Link>{' '}
                  for the above account;
                </Text>
              </List.Item>
              <List.Item>
                <Text>I will comply with the terms of the credit contract.</Text>
                <div style={{ height: '14px' }} />
                <Text>
                  I acknowledge that: (i) the information provided in this application is true and correct and that no
                  information which is or could be relevant to Latitude making me an additional cardholder has been
                  withheld; and (ii) I have read and understood the terms of the credit contract provided to the primary
                  cardholder at the time of application.
                </Text>
              </List.Item>
              <List.Item>
                <Text>
                  I understand that Latitude collects and uses the information provided about me to facilitate this
                  authorisation. Further information on how Latitude collects, uses, discloses and stores personal
                  information including how I can access and seek correction of my personal information or complain
                  about Latitude's handling of my personal information I can refer to the Privacy Policy available at{' '}
                  <Link href="https://www.gemfinance.co.nz/privacy" newTab>
                    https://www.gemfinance.co.nz/privacy
                  </Link>
                  .
                </Text>
              </List.Item>
            </List>
          </Stack>
        </div>
      </div>

      <Stack gap="4">
        <div style={{ marginTop: '32px' }}>
          <Text textStyle="heading5">
            <strong>Important Information Notice</strong>
          </Text>
        </div>
        <Text>
          You will be directed to some verification options on the next screen, which include checks such as a
          driver licence check, or to check your credit report for identification verification purposes only
          (please note, this does not affect your credit report). You can choose not to consent to these
          verification checks.
        </Text>

        <Text>
          The information you provide in your application is collected and stored by Latitude. Some of that
          information; your name, date of birth, address and reference numbers such as your driver licence number
          (together referred to as your personal information) is disclosed to Green ID. Green ID is owned and
          operated by GBG ANZ Pty Ltd which provides electronic identification verification services to Latitude. To
          enable Latitude to verify your identity and meet its legal obligations under Anti-Money Laundering laws,
          Latitude discloses your personal information to Green ID and then Green ID collects, uses and discloses
          your personal information to each of the data sources that you select when you use Green ID. Latitude does
          not collect, use, nor store any of the reference numbers that you disclose within any of the data sources
          that you select and use as part of the Green ID electronic identity verification process.
        </Text>
      </Stack>

      <DocumentIssuerModal isOpen={isDocumentIssuerModalOpen} onClose={() => setIsDocumentIssuerModalOpen(false)} />
      <CreditReportingBodiesModal
        isOpen={isCreditReportingBodiesModalOpen}
        onClose={() => setIsCreditReportingBodiesModalOpen(false)}
      />
    </>
  );
}

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};
function DocumentIssuerModal({ isOpen, onClose }: ModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div style={{ padding: '10px' }}>
        <Stack gap="8" padding="9">
          <Text textStyle="heading4">ID check with the document issuer</Text>
          <Text>
            If you agree to an ID check with the document issuer we will use the document details you have entered
            (either your driver licence or passport details) and check them with the issuer of that document using third
            party systems, to verify your identity.
          </Text>
          <Container textAlign="center">
            <Button onClick={onClose}>Close</Button>
          </Container>
        </Stack>
      </div>
    </Modal>
  );
}

function CreditReportingBodiesModal({ isOpen, onClose }: ModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div style={{ padding: '10px' }}>
        <Stack gap="8" padding="9">
          <Text textStyle="heading4">Credit reporting bodies</Text>
          <Text>
            Latitude is required by the Anti-Money Laundering & Counter-Terrorism Financing Act 2006 to verify your
            identity before providing you with a product or service.
          </Text>
          <Text>
            By acknowledging this statement, you agree you have read this information and allow Latitude to provide
            personal information about you to credit reporting bodies to enable verification of your identity.
          </Text>

          <Text>
            Personal information that Latitude may provide to the credit reporting body includes:
            <List>
              <List.Item>Full Name</List.Item>
              <List.Item>Address</List.Item>
              <List.Item>Date of Birth</List.Item>
            </List>
          </Text>

          <Text>
            You understand that in addition to the circumstances described in the privacy consent, your personal
            information will be used by the credit reporting body and by Latitude to assess whether the information
            provided by Latitude matches the information held about you by the credit reporting body for the purpose of
            verifying your identity. You also understand that use of your personal information in this way{' '}
            <Bold>does not constitute a credit check or impact your credit standing</Bold> in any way.
          </Text>

          <Text>
            The credit reporting body may inform Latitude as to whether the personal information provided is a full or
            partial match with information held by it. Latitude cannot rectify any discrepancies but will advise you by
            letter or email and provide contact details of the credit reporting body where you can confirm your details.
          </Text>
          <Container textAlign="center">
            <Button onClick={onClose}>Close</Button>
          </Container>
        </Stack>
      </div>
    </Modal>
  );
}
